import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../../firebase';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import UpgradeModal from '../UpgradeModal';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;
const HOME_DEPARTMENTS = [
  'Administration', 'Customer Support', 'Engineering', 'Product Management', 'Design / UX', 'Data & Analytics', 'Marketing', 'Sales', 'Finance & Accounting', 'Human Resources', 'Operations', 'IT / Systems', 'Security / InfoSec', 'Legal', 'Compliance', 'Procurement', 'Supply Chain', 'Logistics', 'Manufacturing', 'Quality Assurance', 'Research & Development', 'Business Development', 'Partnerships', 'Strategy', 'Content', 'Communications / PR', 'Community', 'Facilities', 'Real Estate', 'Construction', 'Healthcare', 'Education'
];

const ASSESSMENT_PROMPT_VARIANTS = {
  sjt: 'sjt',
  work_sample: 'workSample',
  data_insight: 'dataInsight',
  video_pitch: 'videoPitch',
  mcq_core: 'mcqCore',
  personality: 'personality',
  behavioral: 'behavioral',
  cognitive: 'cognitive',
  mixed: 'mixed'
};

const FieldLabelWithTooltip = ({ htmlFor, label, tooltip, iconClass = '' }) => (
  <div className="assessment-field-label-row">
    {htmlFor ? (
      <label htmlFor={htmlFor} className="assessment-field-label">
        {iconClass && <i className={iconClass} aria-hidden="true"></i>}
        {uiText(label)}
      </label>
    ) : (
      <span className="assessment-field-label">
        {iconClass && <i className={iconClass} aria-hidden="true"></i>}
        {uiText(label)}
      </span>
    )}
    <span className="assessment-tooltip-wrap">
      <button
        type="button"
        className="assessment-tooltip-trigger"
        aria-label={`${label}: ${tooltip}`}
      >
        ?
      </button>
      <span className="assessment-tooltip-content" role="tooltip">{tooltip}</span>
    </span>
  </div>
);

const AssessmentToggle = ({ id, checked, onChange, label, tooltip }) => (
  <div className={`assessment-toggle-card ${checked ? 'assessment-toggle-card-active' : ''}`}>
    <div className="assessment-toggle-control-row">
      <label htmlFor={id} className="assessment-toggle-label">{uiText(label)}</label>
      <span className="assessment-tooltip-wrap">
        <button
          type="button"
          className="assessment-tooltip-trigger"
          aria-label={`${label}: ${tooltip}`}
        >
          ?
        </button>
        <span className="assessment-tooltip-content" role="tooltip">{tooltip}</span>
      </span>
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="assessment-toggle-input"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </div>
  </div>
);

const AssessmentSectionNav = ({ sections, activeSectionId, onNavigate }) => { useUiLanguage(); return (<nav className="assessment-section-nav" aria-label={uiText("Assessment creation sections")}>
    {sections.map((section) => (
      <button
        key={section.id}
        type="button"
        className={`assessment-section-nav-item ${activeSectionId === section.id ? 'assessment-section-nav-item-active' : ''}`}
        aria-current={activeSectionId === section.id ? 'step' : undefined}
        onClick={() => onNavigate(section.id)}
      >
        <span>{uiText(section.label)}</span>
        <span className="assessment-section-nav-dash" aria-hidden="true"></span>
      </button>
    ))}
  </nav>); };

const NewAssessmentStep = () => {
  useUiLanguage();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { canUseAutoAssessments } = useFeatureAccess();
  const { t, i18n } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [companyUID, setCompanyUID] = useState('');

  // Common form
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('external'); // 'external' | 'jobseeqr'
  const [externalLink, setExternalLink] = useState('');
  const [departments, setDepartments] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [afterId, setAfterId] = useState('');
  const [afterOptions, setAfterOptions] = useState([]);

  // AI Assessment specific
  const [assessmentFormat, setAssessmentFormat] = useState('');
  const [assessmentPrompt, setAssessmentPrompt] = useState('');
  const [gradingRubric, setGradingRubric] = useState('');
  const [numQuestions, setNumQuestions] = useState(5);
  const [config, setConfig] = useState({});
  const [autoScreeningEnabled, setAutoScreeningEnabled] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState('assessment-scope-section');

  // Job-varying assessment states
  const [varyByJob, setVaryByJob] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, jobName: '' });

  useEffect(() => setDepartmentOptions(HOME_DEPARTMENTS), []);

  useEffect(() => {
    const sectionIds = source === 'jobseeqr'
      ? [
          'assessment-scope-section',
          'assessment-format-section',
          'assessment-content-section',
          'assessment-scoring-section',
          'assessment-settings-section',
          'assessment-workflow-section'
        ]
      : ['assessment-scope-section', 'assessment-workflow-section'];

    const updateActiveSection = () => {
      let nextSectionId = sectionIds[0];
      sectionIds.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element && element.getBoundingClientRect().top <= 220) {
          nextSectionId = sectionId;
        }
      });
      const pageBottomReached = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 24;
      if (pageBottomReached) nextSectionId = sectionIds[sectionIds.length - 1];
      setActiveSectionId(nextSectionId);
    };

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);
    return () => {
      window.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [source]);

  // Resolve company
  useEffect(() => {
    const resolveCompany = async () => {
      if (!currentUser) return;
      try {
        const ownerSnap = await get(ref(database, `companies/${currentUser.uid}`));
        if (ownerSnap.exists()) { setCompanyUID(currentUser.uid); return; }
        const collabSnap = await get(ref(database, `collaborators/${currentUser.uid}`));
        if (collabSnap.exists()) setCompanyUID(collabSnap.val().companyId);
      } catch (e) { console.error(e); }
    };
    resolveCompany();
  }, [currentUser]);

  // Load after options
  useEffect(() => {
    const loadAfter = async () => {
      if (!companyUID) return;
      try {
        const translateStepName = (id, fallback) => {
          switch (id) {
            case 'all-applicants-1':
              return t('substages.allApplicants');
            case 'filter-applicants':
              return t('substages.fitApplicants');
            case 'offer-sent':
              return t('substages.offerSent');
            case 'hired-applicants':
              return t('substages.hired');
            case 'move-to-onboarding':
              return t('substages.moveToOnboarding');
            default:
              return fallback;
          }
        };

        // Load workflow steps: start with 'Fit Applicants', then add existing substages
        const options = [{ id: 'filter-applicants', name: translateStepName('filter-applicants', t('substages.fitApplicants')) }];

        const wfSnap = await get(ref(database, `companies/${companyUID}/hiringWorkflow`));
        if (wfSnap.exists()) {
          const wf = wfSnap.val() || {};
          Object.entries(wf).forEach(([stageId, s]) => {
            const substages = s?.substages || {};
            Object.entries(substages).forEach(([sid, sub]) => {
              // Exclude protected substages
              const badIds = ['all-applicants-1', 'filter-applicants', 'offer-sent', 'hired-applicants'];
              if (badIds.includes(sid) || badIds.includes(sub?.id)) return;
              options.push({ id: sid, name: translateStepName(sid, sub?.name || sub?.title || sid) });
            });
          });
        }

        setAfterOptions(options);
      } catch (e) { console.error(e); }
    };
    loadAfter();
  }, [companyUID, i18n.language, t]);

  // Helper to sanitize department names for Firebase keys
  const sanitizeDepartmentKey = (dept) => {
    // Replace forbidden characters: . # $ / [ ]
    return String(dept || '').replace(/[.#$/[\]]/g, '_');
  };

  const normalizeDepartmentName = (dept) => sanitizeDepartmentKey(dept)
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase();

  const isPublishedJob = (job = {}) => (
    job.published === true || job.lifecycleStatus === 'published'
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.department-dropdown') && !event.target.closest('.chip')) {
        setShowDepartmentDropdown(false);
      }
    };

    if (showDepartmentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDepartmentDropdown]);

  const createExternal = async () => {
    const stageId = 'stage-1';
    const substageId = `assessment-${Date.now()}`;
    const payload = {
      id: substageId,
      name: title.trim(),
      type: 'assessment',
      icon: 'fa-solid fa-gears',
      iconClass: 'fa-gears',
      link: externalLink.trim(),
      afterId: afterId || null,
      departments: departments.reduce((acc, d) => ({ ...acc, [sanitizeDepartmentKey(d)]: true }), {})
    };
    await set(ref(database, `companies/${companyUID}/hiringWorkflow/${stageId}/substages/${substageId}`), payload);
  };

  const createWithAI = async () => {
    const stageId = 'stage-1';
    const substageId = `ai-assessment-${Date.now()}`;
    const assessmentId = `assessment-${Date.now()}`;

    const payload = {
      id: substageId,
      name: title.trim(),
      type: 'ai-assessment',
      icon: 'fa-solid fa-robot',
      iconClass: 'fa-robot',
      link: assessmentId,
      afterId: afterId || null,
      departments: departments.reduce((acc, d) => ({ ...acc, [sanitizeDepartmentKey(d)]: true }), {}),
      varyByJob: varyByJob,
      autoScreeningEnabled
    };

    const persistAssessmentAndSubstage = async (finalAssessmentId, assessmentData) => {
      const updates = {
        [`companies/${companyUID}/assessments/${finalAssessmentId}`]: assessmentData,
        [`companies/${companyUID}/hiringWorkflow/${stageId}/substages/${substageId}`]: {
          ...payload,
          link: finalAssessmentId,
          ...(autoScreeningEnabled ? { afterId: 'filter-applicants' } : {})
        }
      };

      if (autoScreeningEnabled) {
        const companySnap = await get(ref(database, `companies/${companyUID}`));
        const companyData = companySnap.val() || {};
        const previous = companyData.assessmentAutomation || {};
        const previousStageId = previous.stageId || 'stage-1';
        const previousStillExists = Boolean(
          companyData.hiringWorkflow?.[previousStageId]?.substages?.[previous.substageId]
        );
        if (previous.substageId && previous.substageId !== substageId && previousStillExists) {
          updates[`companies/${companyUID}/hiringWorkflow/${previousStageId}/substages/${previous.substageId}/autoScreeningEnabled`] = false;
        }
        updates[`companies/${companyUID}/assessmentAutomation`] = {
          enabled: true,
          stageId,
          substageId,
          assessmentId: finalAssessmentId,
          updatedAt: Date.now()
        };
      }

      await update(ref(database), updates);
    };

    try {
      setIsGenerating(true);
      const companyDescSnap = await get(ref(database, `companies/${companyUID}/description`));
      const companyDescription = companyDescSnap.exists() ? companyDescSnap.val() : '';

      if (varyByJob) {
        // ========== JOB-VARYING ASSESSMENT: Generate for all matching published jobs ==========
        const jobsSnap = await get(ref(database, `companies/${companyUID}/posts`));
        const allJobs = jobsSnap.exists()
          ? Object.entries(jobsSnap.val() || {}).filter(([_, job]) => isPublishedJob(job))
          : [];

        // Compare normalized department names, not Firebase display keys. This
        // prevents an unrelated first job from becoming the preview when casing
        // or whitespace differs between the job post and department selector.
        const filteredJobs = departments.length > 0
          ? allJobs.filter(([_, job]) => departments.some(
              dept => normalizeDepartmentName(dept) === normalizeDepartmentName(job.department)
            ))
          : allJobs;

        const generateVersion = async ({
          jobId = null,
          jobData = null,
          progressIndex = 0,
          previewOnly = false
        }) => {
          const jobTitle = jobData?.jobOpening || jobData?.title || '';
          const jobDepartment = jobData?.department || '';
          if (!previewOnly) {
            setGenerationProgress(prev => ({
              ...prev,
              current: progressIndex + 1,
              jobName: jobTitle || t('newAssessmentStep.untitledJob')
            }));
          }

          const resp = await fetch(`${BACKEND_URL}/generate-assessment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: title.trim(),
              assessment_type: assessmentFormat,
              assessment_prompt: assessmentPrompt.trim(),
              departments,
              job_title: jobTitle,
              job_department: jobDepartment,
              job_description: jobData?.jobDescription || '',
              company_description: companyDescription,
              job_id: jobId,
              preview_only: previewOnly,
              num_questions: numQuestions,
              config
            })
          });
          const data = await resp.json();
          if (!resp.ok || !data?.success || !data?.assessment) {
            throw new Error(
              data?.detail
              || data?.error
              || t('newAssessmentStep.generationFailedForJob', { job: jobTitle || t('newAssessmentStep.untitledJob') })
            );
          }
          return { jobId, jobTitle, jobDepartment, data };
        };

        if (filteredJobs.length === 0) {
          // There is no job description to vary against yet. Generate a clearly
          // labelled department-level sample for review, but never store it as a
          // deliverable job version. The first matching job gets its own version
          // from its actual JD before a candidate receives the assessment.
          setGenerationProgress({ current: 0, total: 1, jobName: t('newAssessmentStep.departmentSample') });
          const previewResult = await generateVersion({ previewOnly: true });
          const generatedAt = new Date().toISOString();
          const assessmentData = {
            title: title.trim(),
            varyByJob: true,
            previewPendingJobs: true,
            departments,
            assessment_format: assessmentFormat,
            assessment_prompt: assessmentPrompt.trim(),
            grading_rubric: gradingRubric.trim(),
            previewAssessment: {
              questions: previewResult.data.assessment.questions || [],
              duration_minutes: previewResult.data.assessment.duration_minutes,
              generatedAt,
              scopeDepartments: departments
            },
            baseConfig: {
              assessmentType: assessmentFormat,
              assessmentPrompt: assessmentPrompt.trim(),
              gradingRubric: gradingRubric.trim(),
              departments,
              numQuestions,
              config,
              createdAt: generatedAt
            },
            jobQuestions: {}
          };

          await persistAssessmentAndSubstage(assessmentId, assessmentData);
          return { assessmentId, previewOnly: true };
        }

        setGenerationProgress({ current: 0, total: filteredJobs.length, jobName: '' });

        // Run all generations in parallel
        const results = await Promise.all(
          filteredJobs.map(([jobId, jobData], index) => generateVersion({
            jobId,
            jobData,
            progressIndex: index
          }))
        );

        // Build assessment structure with job-specific questions
        const assessmentData = {
          title: title.trim(),
          varyByJob: true,
          departments,
          assessment_format: assessmentFormat,
          assessment_prompt: assessmentPrompt.trim(),
          grading_rubric: gradingRubric.trim(),
          baseConfig: {
            assessmentType: assessmentFormat,
            assessmentPrompt: assessmentPrompt.trim(),
            gradingRubric: gradingRubric.trim(),
            departments,
            numQuestions,
            config,
            createdAt: new Date().toISOString()
          },
          jobQuestions: {}
        };

        let firstJobId = null;
        for (const result of results) {
          if (result.data?.success && result.data.assessment) {
            assessmentData.jobQuestions[result.jobId] = {
              questions: result.data.assessment.questions || [],
              duration_minutes: result.data.assessment.duration_minutes,
              generatedAt: new Date().toISOString(),
              jobTitle: result.jobTitle,
              jobDepartment: result.jobDepartment
            };
            if (!firstJobId) firstJobId = result.jobId;
          }
        }

        await persistAssessmentAndSubstage(assessmentId, assessmentData);

        return { assessmentId, firstJobId };

      } else {
        // ========== REGULAR SINGLE ASSESSMENT ==========
        // A non-varying assessment follows the employer's brief and remains
        // reusable across jobs. Candidate-specific job context is added later by
        // the private scoring agent.
        const jobDescription = '';

        const resp = await fetch(`${BACKEND_URL}/generate-assessment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: title.trim(),
            assessment_type: assessmentFormat,
            assessment_prompt: assessmentPrompt.trim(),
            departments,
            job_description: jobDescription,
            company_description: companyDescription,
            num_questions: numQuestions,
            config
          })
        });
        const data = await resp.json();

        if (resp.ok && data?.success && data.assessment_id) {
          const assessmentData = {
            ...data.assessment,
            title: title.trim(),
            assessment_format: assessmentFormat,
            assessment_prompt: assessmentPrompt.trim(),
            grading_rubric: gradingRubric.trim(),
            job_description: jobDescription,
            departments,
            baseConfig: {
              assessmentType: assessmentFormat,
              assessmentPrompt: assessmentPrompt.trim(),
              gradingRubric: gradingRubric.trim(),
              departments,
              numQuestions,
              config,
              createdAt: new Date().toISOString()
            }
          };
          await persistAssessmentAndSubstage(data.assessment_id, assessmentData);
          return { assessmentId: data.assessment_id };
        } else {
          throw new Error(data?.error || 'Assessment generation failed');
        }
      }
    } catch (e) {
      console.error('AI generation failed', e);
      alert(uiText('Assessment generation failed. Please try again.'));
      return null;
    } finally {
      setIsGenerating(false);
      setGenerationProgress({ current: 0, total: 0, jobName: '' });
    }
  };

  const save = async () => {
    if (!companyUID) return;
    if (!title.trim()) { alert(t('newAssessmentStep.alerts.enterTitle')); return; }
    try {
      if (source === 'external') {
        if (!externalLink.trim()) { alert(t('newAssessmentStep.alerts.link')); return; }
        await createExternal();
        navigate('/applicants');
      } else {
        if (!assessmentFormat) { alert(t('newAssessmentStep.alerts.selectType')); return; }
        if (!assessmentPrompt.trim()) { alert(t('newAssessmentStep.alerts.enterPrompt')); return; }
        const result = await createWithAI();

        if (!result) {
          // Generation was cancelled or failed
          return;
        }

        // Redirect to the assessment editor
        if (result.firstJobId) {
          // Job-varying assessment: include job ID in query param
          navigate(`/assessment-editor/${result.assessmentId}?job=${result.firstJobId}`);
        } else if (result.assessmentId) {
          // Regular assessment
          navigate(`/assessment-editor/${result.assessmentId}`);
        } else {
          navigate('/applicants');
        }
      }
    } catch (e) {
      console.error('Create assessment step failed', e);
      alert(t('newAssessmentStep.failed'));
    }
  };

  const back = () => navigate('/applicants');

  const updateConfig = (k, v) => setConfig(prev => ({ ...prev, [k]: v }));

  const assessmentPromptVariant = ASSESSMENT_PROMPT_VARIANTS[assessmentFormat] || 'general';
  const assessmentPromptTranslation = (field) => t(
    `newAssessmentStep.assessmentPromptByType.${assessmentPromptVariant}.${field}`
  );

  const assessmentSections = source === 'jobseeqr'
    ? [
        { id: 'assessment-scope-section', label: t('newAssessmentStep.departments') },
        { id: 'assessment-format-section', label: t('newAssessmentStep.assessmentFormat') },
        { id: 'assessment-content-section', label: assessmentPromptTranslation('label') },
        { id: 'assessment-scoring-section', label: t('newAssessmentStep.gradingRubric') },
        { id: 'assessment-settings-section', label: t('newAssessmentStep.settingsSectionTitle') },
        { id: 'assessment-workflow-section', label: t('newAssessmentStep.addAfter') }
      ]
    : [
        { id: 'assessment-scope-section', label: t('newAssessmentStep.departments') },
        { id: 'assessment-workflow-section', label: t('newAssessmentStep.addAfter') }
      ];

  const navigateToAssessmentSection = (sectionId) => {
    setActiveSectionId(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ backgroundColor: 'var(--app-background, #031128)', minHeight: '100vh', color: 'var(--app-text, #FFFFFF)', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', paddingTop: '100px', paddingBottom: '100px', display: 'flex', justifyContent: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
      <style>
        {`
          .new-step-input::placeholder,
          .new-step-select option:first-child {
            color: rgba(var(--app-ink-rgb, 255, 255, 255), 0.4) !important;
          }
          .new-step-select option:not(:first-child) {
            color: var(--app-text, #FFFFFF);
          }
          .chip {
            background-color: var(--app-surface, rgba(255, 255, 255, 0.08));
            border: 1px solid var(--app-border, rgba(255, 255, 255, 0.15));
            color: var(--app-text, #FFFFFF);
            padding: 8px 16px;
            border-radius: 20px;
            cursor: pointer;
            font-family: 'Montserrat';
            font-size: 14px;
            transition: all 0.2s;
          }
          .chip:hover {
            background-color: var(--app-surface-hover, rgba(86, 141, 237, 0.2));
            border-color: #568ded;
          }
          .chip-active {
            background-color: #568ded !important;
            border-color: #568ded !important;
            color: #FFFFFF !important;
          }
          .btn-primary {
            background-color: #568ded;
            color: #FFFFFF;
            border: none;
            border-radius: 25px;
            padding: 12px 32px;
            font-family: 'Montserrat';
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }
          .btn-primary:hover:not(:disabled) {
            background-color: #4a7dd4;
            transform: translateY(-1px);
          }
          .btn-primary:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .assessment-top-level-title {
            margin: 0 0 12px;
            color: var(--app-text, #fff);
            font-family: 'Montserrat';
            font-size: 16px;
            font-weight: var(--app-weight-strong, 700);
            line-height: 1.4;
          }
          .assessment-field-label-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 10px;
          }
          .assessment-field-label {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            margin: 0;
            color: var(--app-text, #fff);
            font-family: 'Montserrat';
            font-size: 15px;
            font-weight: var(--app-weight-strong, 700);
            line-height: 1.4;
          }
          .assessment-anchor {
            scroll-margin-top: 110px;
          }
          .assessment-section-nav {
            position: fixed;
            z-index: 15;
            top: 180px;
            right: 24px;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 11px;
            width: 200px;
          }
          .assessment-section-nav-item {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 28px;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 4px 0;
            border: 0;
            background: transparent;
            color: rgba(var(--app-ink-rgb, 255, 255, 255), 0.62);
            font-family: 'Montserrat';
            font-size: 13px;
            font-weight: 600;
            line-height: 1.35;
            text-align: right;
            direction: ltr;
            cursor: pointer;
            transition: color 0.18s ease;
          }
          .assessment-section-nav-item span:first-child {
            overflow: hidden;
            text-overflow: ellipsis;
            unicode-bidi: plaintext;
          }
          .assessment-section-nav-dash {
            display: block;
            width: 24px;
            height: 2px;
            border-radius: 2px;
            background: var(--app-surface, rgba(255, 255, 255, 0.3));
            transition: width 0.18s ease, background-color 0.18s ease;
          }
          .assessment-section-nav-item:hover,
          .assessment-section-nav-item:focus-visible,
          .assessment-section-nav-item-active {
            color: var(--app-text, #fff);
            outline: none;
          }
          .assessment-section-nav-item:hover .assessment-section-nav-dash,
          .assessment-section-nav-item:focus-visible .assessment-section-nav-dash,
          .assessment-section-nav-item-active .assessment-section-nav-dash {
            width: 28px;
            background: #568ded;
          }
          .assessment-section-nav-item-active {
            font-weight: var(--app-weight-strong, 700);
          }
          @media (max-width: 1140px) {
            .assessment-section-nav {
              display: none;
            }
          }
          .assessment-toggle-card {
            width: 100%;
            box-sizing: border-box;
            padding: 18px;
            border: 1px solid var(--app-border, rgba(255, 255, 255, 0.12));
            border-radius: 12px;
            background: var(--app-surface, rgba(255, 255, 255, 0.04));
            text-align: center;
            transition: background-color 0.2s ease, border-color 0.2s ease;
          }
          .assessment-toggle-card-active {
            border-color: rgba(86, 141, 237, 0.55);
            background: var(--app-surface, rgba(86, 141, 237, 0.11));
          }
          .assessment-toggle-control-row {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            min-height: 30px;
          }
          .assessment-toggle-label {
            margin: 0;
            color: var(--app-text, #fff);
            font-family: 'Montserrat';
            font-size: 14px;
            font-weight: var(--app-weight-strong, 700);
            line-height: 1.4;
            text-align: center;
            cursor: pointer;
          }
          .assessment-toggle-input {
            appearance: none;
            -webkit-appearance: none;
            position: relative;
            flex: 0 0 auto;
            width: 48px;
            height: 26px;
            margin: 0;
            border: 1px solid var(--app-border, rgba(255, 255, 255, 0.2));
            border-radius: 999px;
            background: var(--app-surface, rgba(255, 255, 255, 0.16));
            cursor: pointer;
            transition: background-color 0.2s ease, border-color 0.2s ease;
          }
          .assessment-toggle-input::after {
            content: '';
            position: absolute;
            top: 2px;
            left: 2px;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #fff;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
            transition: transform 0.2s ease;
          }
          .assessment-toggle-input:checked {
            border-color: #568ded;
            background: #568ded;
          }
          .assessment-toggle-input:checked::after {
            transform: translateX(22px);
          }
          .assessment-toggle-input:focus-visible,
          .assessment-tooltip-trigger:focus-visible {
            outline: 2px solid #8fb6ff;
            outline-offset: 3px;
          }
          .assessment-tooltip-wrap {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .assessment-tooltip-trigger {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            padding: 0;
            border: 1px solid rgba(143, 182, 255, 0.65);
            border-radius: 50%;
            background: var(--app-surface, rgba(86, 141, 237, 0.16));
            color: var(--app-text, #b9d1ff);
            font-size: 12px;
            font-weight: var(--app-weight-strong, 800);
            cursor: help;
          }
          .assessment-tooltip-content {
            visibility: hidden;
            opacity: 0;
            position: absolute;
            z-index: 20;
            bottom: calc(100% + 10px);
            left: 50%;
            width: min(320px, 75vw);
            padding: 11px 13px;
            transform: translateX(-50%) translateY(4px);
            border: 1px solid rgba(143, 182, 255, 0.35);
            border-radius: 9px;
            background: var(--app-surface, #0b1d3d);
            color: var(--app-text, #fff);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.38);
            font-size: 12px;
            font-weight: 500;
            line-height: 1.5;
            text-align: center;
            pointer-events: none;
            transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
          }
          .assessment-tooltip-wrap:hover .assessment-tooltip-content,
          .assessment-tooltip-wrap:focus-within .assessment-tooltip-content {
            visibility: visible;
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        `}
      </style>

      <AssessmentSectionNav
        sections={assessmentSections}
        activeSectionId={activeSectionId}
        onNavigate={navigateToAssessmentSection}
      />

      <div style={{ width: '100%', maxWidth: '60%', display: 'flex', flexDirection: 'column' }}>
        <button className="post-job-cta" onClick={back} style={{ marginBottom: '40px', order: 0 }}>
          <i className={`fa-solid ${isRTL ? 'fa-circle-chevron-right' : 'fa-circle-chevron-left'}`}></i>
          <span>{t('newInterviewStep.backToATS')}</span>
        </button>

        <div style={{ marginBottom: '40px', order: 1 }}>
          <h1 style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontWeight: "var(--app-weight-strong, 900)", fontSize: '30px', margin: 0 }}>{t('newAssessmentStep.title')}</h1>
          <p style={{ opacity: 0.7, marginTop: '10px', marginBottom: 0 }}>{t('newAssessmentStep.subtitle')}</p>
        </div>

        {/* 1. Title */}
        <div style={{ marginBottom: '40px', order: 2 }}>
          <p className="assessment-top-level-title">{t('newAssessmentStep.stepTitle')}</p>
          <input
            type="text"
            placeholder={t('newAssessmentStep.titlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="new-step-input"
            style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', outline: 'none' }}
          />
        </div>

        {/* 2. Source */}
        <div style={{ marginBottom: '40px', order: 3 }}>
          <p className="assessment-top-level-title">{t('newAssessmentStep.sourceLabel')}</p>
          <div style={{ display: 'flex', gap: '10px', marginBottom: source === 'external' ? '10px' : '0' }}>
            <button type="button" className={`chip ${source === 'external' ? 'chip-active' : ''}`} onClick={() => setSource('external')}>{t('newAssessmentStep.sourceExternal')}</button>
            <button
              type="button"
              className={`chip ${source === 'jobseeqr' ? 'chip-active' : ''}`}
              onClick={() => canUseAutoAssessments ? setSource('jobseeqr') : setShowUpgradeModal(true)}
            >
              {t('newAssessmentStep.sourceJobseeqr')}
            </button>
          </div>
          {source === 'external' && (
            <input
              type="text"
              placeholder={t('newAssessmentStep.externalPlaceholder')}
              value={externalLink}
              onChange={(e) => setExternalLink(e.target.value)}
              className="new-step-input"
              style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', outline: 'none', marginTop: '10px' }}
            />
          )}
        </div>

        {source === 'jobseeqr' && (
          <div style={{ marginBottom: '40px', order: 5, display: 'flex', flexDirection: 'column' }}>
            <div id="assessment-format-section" className="assessment-anchor" style={{ marginBottom: '40px', order: 1 }}>
              <FieldLabelWithTooltip
                htmlFor="assessment-format"
                label={t('newAssessmentStep.assessmentFormat')}
                tooltip={t('newAssessmentStep.assessmentFormatHelp')}
              />
              <select
                id="assessment-format"
                value={assessmentFormat}
                onChange={(event) => setAssessmentFormat(event.target.value)}
                className="new-step-select"
                style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', outline: 'none' }}
              >
                <option value="">{t('newAssessmentStep.selectTypePlaceholder')}</option>
                <option value="sjt">{t('addSubstage.assessmentTypes.sjt')}</option>
                <option value="work_sample">{t('addSubstage.assessmentTypes.workSample')}</option>
                <option value="data_insight">{t('addSubstage.assessmentTypes.dataInsight')}</option>
                <option value="video_pitch">{t('addSubstage.assessmentTypes.videoPitch')}</option>
                <option value="mcq_core">{t('addSubstage.assessmentTypes.mcqCore')}</option>
                <option value="personality">{t('addSubstage.assessmentTypes.personality')}</option>
                <option value="behavioral">{t('addSubstage.assessmentTypes.behavioral')}</option>
                <option value="cognitive">{t('addSubstage.assessmentTypes.cognitive')}</option>
                <option value="mixed">{t('addSubstage.assessmentTypes.mixed')}</option>
              </select>
            </div>

            <div id="assessment-content-section" className="assessment-anchor" style={{ marginBottom: '40px', order: 2 }}>
              <FieldLabelWithTooltip
                htmlFor="assessment-prompt"
                label={assessmentPromptTranslation('label')}
                tooltip={`${assessmentPromptTranslation('help')} ${departments.length > 0
                  ? t('newAssessmentStep.assessmentScopeDepartments', { departments: departments.join(', ') })
                  : t('newAssessmentStep.assessmentScopeAll')}`}
              />
              <textarea
                id="assessment-prompt"
                value={assessmentPrompt}
                onChange={(event) => setAssessmentPrompt(event.target.value)}
                placeholder={assessmentPromptTranslation('placeholder')}
                className="new-step-input"
                rows={6}
                style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', lineHeight: 1.6, outline: 'none', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '40px', order: 3 }}>
              <FieldLabelWithTooltip
                htmlFor="format-instructions"
                label={t('newAssessmentStep.formatInstructions')}
                tooltip={t('newAssessmentStep.formatInstructionsHelp')}
              />
              <textarea
                id="format-instructions"
                value={config.formatInstructions || ''}
                onChange={(event) => updateConfig('formatInstructions', event.target.value)}
                placeholder={t('newAssessmentStep.formatInstructionsPlaceholder')}
                className="new-step-input"
                rows={3}
                style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', lineHeight: 1.6, outline: 'none', resize: 'vertical' }}
              />
            </div>

            <div id="assessment-scoring-section" className="assessment-anchor" style={{ marginBottom: '40px', order: 6 }}>
              <FieldLabelWithTooltip
                htmlFor="grading-rubric"
                label={t('newAssessmentStep.gradingRubric')}
                tooltip={t('newAssessmentStep.gradingRubricHelp')}
                iconClass="fa-solid fa-lock"
              />
              <textarea
                id="grading-rubric"
                value={gradingRubric}
                onChange={(event) => setGradingRubric(event.target.value)}
                placeholder={t('newAssessmentStep.gradingRubricPlaceholder')}
                className="new-step-input"
                rows={6}
                style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', lineHeight: 1.6, outline: 'none', resize: 'vertical' }}
              />
            </div>

            <div id="assessment-settings-section" className="assessment-anchor" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px', marginBottom: '40px', order: 8 }}>
              <div>
                <p style={{ margin: 0, marginBottom: '10px' }}>{t('newAssessmentStep.timeLimit')}</p>
                <input
                  type="number"
                  min="1"
                  value={config.duration || ''}
                  onChange={(e) => updateConfig('duration', e.target.value)}
                  className="new-step-input"
                  placeholder={t('newAssessmentStep.timeLimitPlaceholder')}
                  style={{ width: '200px', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', outline: 'none' }}
                />
              </div>
              <div>
                <p style={{ margin: 0, marginBottom: '10px' }}>{t('newAssessmentStep.numQuestions')}</p>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(parseInt(e.target.value) || 5)}
                  className="new-step-input"
                  style={{ width: '200px', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', outline: 'none' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '40px', order: 9 }}>
              <AssessmentToggle
                id="vary-assessment-by-job"
                checked={varyByJob}
                onChange={setVaryByJob}
                label={t('newAssessmentStep.varyByJob')}
                tooltip={t('newAssessmentStep.varyByJobHelp')}
              />
            </div>

            <div style={{ marginBottom: '40px', order: 10 }}>
              <AssessmentToggle
                id="include-assessment-in-auto-screening"
                checked={autoScreeningEnabled}
                onChange={setAutoScreeningEnabled}
                label={t('newAssessmentStep.autoScreening')}
                tooltip={t('newAssessmentStep.autoScreeningHelp')}
              />
            </div>

            {isGenerating && generationProgress.total > 0 && (
                  <div style={{
                    order: 11,
                    backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.15))',
                    border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
                    borderRadius: '12px',
                    padding: '16px 20px',
                    marginBottom: '30px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                      <i className="fa-solid fa-spinner fa-spin" style={{ color: '#568ded', fontSize: '18px' }}></i>
                      <span style={{ fontFamily: 'Montserrat', fontSize: '14px', color: 'var(--app-text, #fff)' }}>{uiText("Generating assessments for all jobs...")}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '13px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)' }}>
                        {generationProgress.jobName && uiText("Current: {{title}}", { title: generationProgress.jobName })}
                      </span>
                      <span style={{ fontSize: '13px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)' }}>
                        {generationProgress.current} / {generationProgress.total}
                      </span>
                    </div>
                    <div style={{
                      height: '6px',
                      backgroundColor: 'var(--app-surface, rgba(255,255,255,0.1))',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${(generationProgress.current / generationProgress.total) * 100}%`,
                        backgroundColor: '#568ded',
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
            )}
          </div>
        )}

        {/* Departments */}
        <div id="assessment-scope-section" className="assessment-anchor" style={{ marginBottom: '40px', order: 4 }}>
          <FieldLabelWithTooltip
            label={t('newAssessmentStep.departments')}
            tooltip={t('newAssessmentStep.departmentScopeHelp')}
          />
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <button
              type="button"
              className={`chip ${departments.length === 0 ? 'chip-active' : ''}`}
              onClick={() => {
                setDepartments([]);
                setShowDepartmentDropdown(false);
              }}
            >
              {t('newAssessmentStep.allDepartments')}
            </button>
            <button
              type="button"
              className={`chip ${showDepartmentDropdown || departments.length > 0 ? 'chip-active' : ''}`}
              onClick={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
            >
              {t('newAssessmentStep.selectDepartments')}
            </button>
          </div>
          {(departments.length > 0 || showDepartmentDropdown) && (
            <div className="department-dropdown" style={{
              position: 'relative',
              width: '400px'
            }}>
              {/* Selected departments as chips */}
              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                marginBottom: '8px',
                justifyContent: 'flex-start'
              }}>
                {departments.map(dept => (
                  <div key={dept} style={{
                    backgroundColor: '#568ded',
                    color: '#FFFFFF',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontFamily: 'Montserrat',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {dept}
                    <i
                      className="fa-solid fa-times"
                      style={{ cursor: 'pointer', fontSize: '10px' }}
                      onClick={() => setDepartments(prev => prev.filter(d => d !== dept))}
                    ></i>
                  </div>
                ))}
              </div>

              {/* Dropdown */}
              <div style={{
                position: 'relative',
                width: '100%',
                backgroundColor: 'var(--app-surface, #0f2349)',
                border: "1px solid var(--app-border, rgba(255,255,255,0.08))",
                borderRadius: '8px',
                padding: '8px 12px',
                minHeight: '150px',
                maxHeight: '250px',
                overflowY: 'auto',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}>
                {departmentOptions.map(d => (
                  <div key={d} style={{
                    display: 'grid',
                    gridTemplateColumns: '18px 1fr',
                    alignItems: 'center',
                    columnGap: '6px',
                    padding: '6px 8px',
                    cursor: 'pointer',
                    color: 'var(--app-text, #FFFFFF)',
                    fontFamily: 'Montserrat',
                    fontSize: '13px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s',
                    textAlign: 'left',
                    justifyItems: 'start'
                  }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.1))')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    onClick={() => {
                      setDepartments(prev =>
                        prev.includes(d)
                          ? prev.filter(x => x !== d)
                          : [...prev, d]
                      );
                    }}>
                    <input
                      type="checkbox"
                      checked={departments.includes(d)}
                      onChange={() => { }} // Handled by parent onClick
                      style={{
                        accentColor: '#568ded',
                        width: '16px',
                        height: '16px',
                        margin: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ lineHeight: '16px' }}>{d}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Add after */}
        <div id="assessment-workflow-section" className="assessment-anchor" style={{ marginBottom: '40px', order: 6 }}>
          <p className="assessment-top-level-title">{t('newAssessmentStep.addAfter')}</p>
          <select
            value={afterId}
            onChange={(e) => setAfterId(e.target.value)}
            className="new-step-select"
            style={{ width: '100%', backgroundColor: 'var(--app-surface, #0f2349)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", color: 'var(--app-text, #fff)', borderRadius: '12px', padding: '14px 16px', fontFamily: 'Montserrat', fontSize: '14px', outline: 'none' }}
          >
            <option value="">{t('newAssessmentStep.selectStep')}</option>
            {afterOptions.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', order: 7 }}>
          <button className="btn-primary" onClick={save} disabled={isGenerating}>
            <span style={{ marginRight: '8px' }}>{isGenerating ? t('newAssessmentStep.creating') : t('newAssessmentStep.createStep')}</span>
            <i className="fa-solid fa-circle-chevron-right"></i>
          </button>
        </div>

        {showUpgradeModal && (
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            requiredFeature="autoAssessments"
            featureDisplayName={t('newAssessmentStep.upgradeName')}
          />
        )}
      </div>
    </div>
  );
};

export default NewAssessmentStep;
